import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './TeacherAttendanceView.css';

function TeacherAttendanceView({ teacherData }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    console.log('🔥 TeacherAttendance: Loading classes for teacher', teacherData.uid);
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherAttendance: Classes loaded', classesData.length, classesData);
      setClasses(classesData);
      if (classesData.length > 0) {
        setSelectedClassId(classesData[0].id);
        console.log('🔥 TeacherAttendance: Selected class ID', classesData[0].id);
      }
    } catch (error) {
      console.error('🔥 TeacherAttendance: Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherData]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 TeacherAttendance: Loading students for class', selectedClassId);
    try {
      const studentsQuery = query(
        collection(db, 'classes', selectedClassId, 'students'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(studentsQuery);
      const studentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherAttendance: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 TeacherAttendance: Error loading students:', error);
    }
  }, [selectedClassId]);

  const loadAttendance = useCallback(async () => {
    // Match the ISO format used by iOS: YYYY-MM-DDTHH:mm:ssTZD (with local timezone)
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    // Format to match iOS timezone format (e.g., 2026-06-29T00:00:00-04:00)
    const offset = startOfDay.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offset / 60));
    const offsetMinutes = Math.abs(offset % 60);
    const offsetSign = offset <= 0 ? '+' : '-';
    const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
    const dateStr = `${startOfDay.getFullYear()}-${String(startOfDay.getMonth() + 1).padStart(2, '0')}-${String(startOfDay.getDate()).padStart(2, '0')}T00:00:00${offsetStr}`;
    console.log('🔥 TeacherAttendance: Loading attendance for class', selectedClassId, 'date', dateStr);
    try {
      const attendanceQuery = query(
        collection(db, 'classes', selectedClassId, 'attendanceRecords'),
        where('date', '==', dateStr)
      );
      const snapshot = await getDocs(attendanceQuery);
      const records = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log('🔥 TeacherAttendance: Attendance record', data);
        records[data.studentId] = data.status;
      });
      console.log('🔥 TeacherAttendance: Attendance loaded', Object.keys(records).length, records);
      setAttendanceRecords(records);
    } catch (error) {
      console.error('🔥 TeacherAttendance: Error loading attendance:', error);
    }
  }, [selectedClassId, selectedDate]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (selectedClassId) {
      loadStudents();
      loadAttendance();
    }
  }, [selectedClassId, selectedDate, loadStudents, loadAttendance]);

  const handleAttendanceChange = (studentId, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const saveAttendance = async () => {
    setSaving(true);
    try {
      // Match the ISO format used by iOS: YYYY-MM-DDTHH:mm:ssTZD (with local timezone)
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      // Format to match iOS timezone format (e.g., 2026-06-29T00:00:00-04:00)
      const offset = startOfDay.getTimezoneOffset();
      const offsetHours = Math.abs(Math.floor(offset / 60));
      const offsetMinutes = Math.abs(offset % 60);
      const offsetSign = offset <= 0 ? '+' : '-';
      const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
      const dateStr = `${startOfDay.getFullYear()}-${String(startOfDay.getMonth() + 1).padStart(2, '0')}-${String(startOfDay.getDate()).padStart(2, '0')}T00:00:00${offsetStr}`;

      const now = new Date();
      const nowOffset = now.getTimezoneOffset();
      const nowOffsetHours = Math.abs(Math.floor(nowOffset / 60));
      const nowOffsetMinutes = Math.abs(nowOffset % 60);
      const nowOffsetSign = nowOffset <= 0 ? '+' : '-';
      const nowOffsetStr = `${nowOffsetSign}${String(nowOffsetHours).padStart(2, '0')}:${String(nowOffsetMinutes).padStart(2, '0')}`;
      const nowStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}${nowOffsetStr}`;

      const batch = Object.entries(attendanceRecords).map(async ([studentId, status]) => {
        // Deterministic ID per student+class+date so re-marking the same day
        // updates the record instead of creating duplicates.
        const dayKey = `${startOfDay.getFullYear()}-${String(startOfDay.getMonth() + 1).padStart(2, '0')}-${String(startOfDay.getDate()).padStart(2, '0')}`;
        const recordId = `${studentId}_${dayKey}`;
        const recordRef = doc(
          db,
          'classes',
          selectedClassId,
          'attendanceRecords',
          recordId
        );
        await setDoc(recordRef, {
          id: recordId,
          studentId,
          classId: selectedClassId,
          date: dateStr,
          status: status,
          notes: '',
          markedBy: teacherData?.uid,
          eventId: null,
          createdAt: nowStr,
          updatedAt: nowStr,
          schoolId: teacherData?.schoolId || null
        }, { merge: true });
      });
      await Promise.all(batch);
      alert('Attendance saved successfully!');
      loadAttendance(); // Reload to show saved data
    } catch (error) {
      console.error('Error saving attendance:', error);
      alert('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="teacher-attendance-view">
      <h2>Attendance Marking</h2>

      <div className="date-selector">
        <button onClick={() => changeDate(-1)} className="date-nav-button">←</button>
        <div className="date-display">
          <div className="date-text">{formatDate(selectedDate)}</div>
          {new Date().toDateString() === selectedDate.toDateString() && (
            <div className="today-badge">Today</div>
          )}
        </div>
        <button onClick={() => changeDate(1)} className="date-nav-button">→</button>
      </div>

      {classes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏫</div>
          <h3>No Classes Assigned</h3>
          <p>You don't have any classes assigned yet.</p>
        </div>
      ) : (
        <>
          <div className="class-selector">
            <label>Select Class:</label>
            <select
              value={selectedClassId || ''}
              onChange={(e) => setSelectedClassId(e.target.value)}
            >
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {cls.name} ({cls.grade})
                </option>
              ))}
            </select>
          </div>

          {students.length === 0 ? (
            <div className="empty-state">
              <p>No students in this class</p>
            </div>
          ) : (
            <>
              <div className="total-students-summary">
                <div className="summary-item total">
                  <span className="count">{students.length}</span>
                  <span className="label">Total Students in Class</span>
                </div>
              </div>

              <div className="attendance-summary">
                <div className="summary-item present">
                  <span className="count">{Object.values(attendanceRecords).filter(s => s === 'Present').length}</span>
                  <span className="label">Present</span>
                </div>
                <div className="summary-item absent">
                  <span className="count">{Object.values(attendanceRecords).filter(s => s === 'Absent').length}</span>
                  <span className="label">Absent</span>
                </div>
                <div className="summary-item late">
                  <span className="count">{Object.values(attendanceRecords).filter(s => s === 'Late').length}</span>
                  <span className="label">Late</span>
                </div>
                <div className="summary-item unmarked">
                  <span className="count">{students.length - Object.keys(attendanceRecords).length}</span>
                  <span className="label">Unmarked</span>
                </div>
              </div>

              <div className="students-list">
                {students.map(student => (
                  <div key={student.id} className="student-row">
                    <div className="student-info">
                      {student.photoURL ? (
                        <img src={student.photoURL} alt={student.fullName} className="student-photo" />
                      ) : (
                        <div className="student-photo placeholder">👤</div>
                      )}
                      <div>
                        <div className="student-name">{student.fullName}</div>
                        <div className="student-id">ID: {student.studentId}</div>
                      </div>
                    </div>
                    <div className="attendance-buttons">
                      <button
                        className={`attendance-btn present ${attendanceRecords[student.id] === 'Present' ? 'active' : ''}`}
                        onClick={() => handleAttendanceChange(student.id, 'Present')}
                      >
                        ✓ Present
                      </button>
                      <button
                        className={`attendance-btn absent ${attendanceRecords[student.id] === 'Absent' ? 'active' : ''}`}
                        onClick={() => handleAttendanceChange(student.id, 'Absent')}
                      >
                        ✗ Absent
                      </button>
                      <button
                        className={`attendance-btn late ${attendanceRecords[student.id] === 'Late' ? 'active' : ''}`}
                        onClick={() => handleAttendanceChange(student.id, 'Late')}
                      >
                        ⏰ Late
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="save-section">
                <button
                  className="save-button"
                  onClick={saveAttendance}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Attendance'}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default TeacherAttendanceView;
